import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PrintInvoice from './PrintInvoice'; // Importer den utskriftsvennlige komponenten
import CustomerServiceDescription from './CustomerServiceDescription';
import ServiceCustomerInfo from './ServiceCustomerInfo';
import { ServiceProductInfo } from './ServiceProductInfo';
import { ServiceTypeSelect } from './ServiceTypeSelect';
import calculateTotalPartsPrice from './hjelpere/calculateTotalPartsPrice';
import { getButikkKapasiteter, getKapasitetsOverstyringer, getSkiSkoyteTjenester } from './lib/tjenestekatalogApi';
import { normalizeWorkshopSlug, isoDate } from './components/workshop/workshopCapacityUtils';
import { computeFerdigDatoForskslag, summerArbeidForKapasitet } from './components/workshop/skiCapacityLogic';
import { escapeHtml } from './utils/format';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ServiceDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Ny loading state
    const [isLoading, setIsLoading] = useState(true);

    const [formData, setFormData] = useState({
        servicetype: 'Skiservice',
        beskrivelse: '',
        arbeid: [], // Liste over flere arbeid
        deler: [], // Ny array for deler
        utførtArbeid: '', // Kommentarer for utført arbeid
        status: 'Aktiv',
        ansatt: '',
        Varemerke: '',
        Produkt: '',
        Størrelse: '',
        Farge: '',
    });
    const [fixedPrices, setFixedPrices] = useState([]);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [updateMessage, setUpdateMessage] = useState('');
    const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [customWork, setCustomWork] = useState({ title: '', price: '' }); // Nytt state for egendefinert arbeid
    const [selectedWork, setSelectedWork] = useState(null);
    const [customPart, setCustomPart] = useState({
        ean: '',
        brand: '',
        product: '',
        price: '',
        discount: '',
    });
    const [parts, setParts] = useState([]); // State for lagring av alle deler
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredParts, setFilteredParts] = useState([]);
    const [isWorkEmpty, setIsWorkEmpty] = useState(false); // Ny tilstand for å sjekke om arbeid er tom
    const [beregnerFerdigdato, setBeregnerFerdigdato] = useState(false);
    const [ferdigdatoMelding, setFerdigdatoMelding] = useState('');

    // Scroll til toppen ved første innlasting
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Funksjon som sjekker beskrivelse når siden lastes
    useEffect(() => {
        if (!formData.beskrivelse.trim()) {
            setIsDescriptionEmpty(true); // Sett isDescriptionEmpty til true hvis beskrivelse er tom
        }
    }, [formData.beskrivelse]);

    // Funksjon som sjekker om arbeid-arrayen er tom
    useEffect(() => {
        if (formData.arbeid.length === 0) {
            setIsWorkEmpty(true); // Sett isWorkEmpty til true hvis arbeid-arrayen er tom
        } else {
            setIsWorkEmpty(false); // Skjul feilmeldingen hvis arbeid-arrayen ikke er tom
        }
    }, [formData.arbeid]);

    const handleSelectWork = (e) => {
        const work = fixedPrices.find(
            (price) => price._id === e.target.value && price.serviceType === 'Skiservice'
        );
        setSelectedWork(work); // Lagre det valgte arbeidet midlertidig
    };

    // Funksjon for å filtrere deler basert på søk, minimum 2 bokstaver
    const handleSearchParts = (e) => {
        const searchValue = e.target.value.toLowerCase();
        setSearchTerm(searchValue);

        if (searchValue.length >= 2) {
            const filtered = parts.filter(
                (part) =>
                    part.ean.toLowerCase().includes(searchValue) ||
                    part.product.toLowerCase().includes(searchValue) ||
                    part.brand.toLowerCase().includes(searchValue)
            );
            setFilteredParts(filtered);
        } else {
            setFilteredParts([]); // Fjern treff hvis søket er kortere enn 2 bokstaver
        }
    };

    // Funksjon for å legge til del
    const handleAddPartToWork = async (selectedPart) => {
        const updatedParts = [...formData.deler, selectedPart];

        setFormData((prevData) => ({
            ...prevData,
            deler: updatedParts,
        }));

        // Nullstill søketerm og fjern nedtrekkslisten
        setSearchTerm(''); // Nullstiller søkefeltet
        setFilteredParts([]); // Tømmer søkeresultatene

        // Oppdater backend
        try {
            const updatedService = { deler: updatedParts };
            await fetch(`${API_BASE_URL}/services/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedService),
            });
        } catch (error) {
            console.error('Feil ved oppdatering av deler:', error);
        }
    };

    // Legg til denne useEffect for å håndtere tidsstyring av meldingen
    useEffect(() => {
        if (updateMessage) {
            const timer = setTimeout(() => {
                setUpdateMessage(''); // Fjern meldingen etter 5 sekunder
            }, 5000);

            // Rydd opp etter timeren når komponenten blir avmontert eller når meldingen endres
            return () => clearTimeout(timer);
        }
    }, [updateMessage]);

    // Hent deler fra serveren (nytt useEffect)
    useEffect(() => {
        const fetchParts = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/parts`);
                if (!response.ok) {
                    throw new Error(`Feil ved henting av deler: ${response.statusText}`);
                }
                const partsData = await response.json();
                setParts(partsData);
                setIsLoading(false); // Sett isLoading til false etter at data er hentet
            } catch (error) {
                console.error('Feil ved henting av deler:', error);
                setIsLoading(false); // Selv om det feiler, sett isLoading til false
            }
        };

        fetchParts();
    }, []);

    // Hent ski-/skøytekatalogen (tjenestekatalogen, ikke lenger FixedPrice)
    useEffect(() => {
        const fetchTjenester = async () => {
            try {
                const tjenester = await getSkiSkoyteTjenester();
                setFixedPrices(tjenester);
            } catch (error) {
                console.error('Feil ved henting av ski-/skøytetjenester:', error);
            }
        };

        fetchTjenester();
    }, []);

    // Hent deler fra serveren (nytt useEffect)
    useEffect(() => {
        const fetchParts = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/parts`); // URL til serveren din
                if (!response.ok) {
                    throw new Error(`Feil ved henting av deler: ${response.statusText}`);
                }
                const partsData = await response.json();
                setParts(partsData); // Sett delene i state
            } catch (error) {
                console.error('Feil ved henting av deler:', error); // Logg eventuelle feil
            }
        };

        fetchParts();
    }, []);

    const handleAddSelectedWork = async () => {
        if (selectedWork) {
            const updatedWork = [
                ...formData.arbeid,
                {
                    title: selectedWork.title,
                    price: selectedWork.price,
                    description: selectedWork.description || 'Ingen beskrivelse tilgjengelig',
                    tidsbruk: selectedWork.tidsbruk || 0,
                    leveringstidDager: selectedWork.leveringstidDager ?? null,
                    samlesUkentlig: !!selectedWork.samlesUkentlig,
                },
            ];

            setFormData((prevData) => ({
                ...prevData,
                arbeid: updatedWork,
            }));

            // Oppdater backend
            try {
                const updatedService = {
                    arbeid: updatedWork.map((work) => ({
                        title: work.title,
                        price: work.price,
                        description: work.description,
                        tidsbruk: work.tidsbruk,
                        leveringstidDager: work.leveringstidDager,
                        samlesUkentlig: work.samlesUkentlig,
                    })),
                };

                await fetch(`${API_BASE_URL}/services/${id}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updatedService),
                });
            } catch (error) {
                console.error('Feil ved oppdatering av arbeid:', error);
            }

            setSelectedWork(null); // Nullstill det valgte arbeidet
        }
    };

    // Regner ut forslag til ferdigdato ut fra arbeidet som er lagt til,
    // butikkens ski-kapasitet (Hygraph) og hva som allerede ligger i køen
    // (backend). Fyller kun inn dato/tid i skjemaet — lagres først når
    // brukeren trykker "Oppdater".
    const handleForeslaFerdigdato = async () => {
        if (!serviceDetails?.butikkid) {
            setFerdigdatoMelding('Mangler butikk på denne servicen — kan ikke beregne ferdigdato.');
            return;
        }
        if (!formData.arbeid.length) {
            setFerdigdatoMelding('Legg til minst ett arbeid før ferdigdato kan beregnes.');
            return;
        }

        setBeregnerFerdigdato(true);
        setFerdigdatoMelding('');
        try {
            const [storesRes, butikkerAll, overridesAll] = await Promise.all([
                fetch(`${API_BASE_URL}/stores`).then((r) => r.json()),
                getButikkKapasiteter(),
                getKapasitetsOverstyringer(),
            ]);

            const store = (storesRes || []).find((s) => s.butikkid === serviceDetails.butikkid);
            const verkstedSlug = normalizeWorkshopSlug(store?.butikknavn || '');
            const cap = (butikkerAll || []).find(
                (b) => normalizeWorkshopSlug(b.lokasjonsNavn || b.slug) === verkstedSlug
            );
            if (!cap) {
                setFerdigdatoMelding('Fant ingen ski-kapasitet satt opp for denne butikken under Tjenester og priser → Kapasitet.');
                return;
            }

            const today = new Date();
            const to = new Date(today.getTime() + 140 * 86400000);
            const brukt = await fetch(
                `${API_BASE_URL}/ski-capacity?butikkid=${serviceDetails.butikkid}&from=${isoDate(today)}&to=${isoDate(to)}`
            ).then((r) => r.json());

            const { totalMin, samlesUkentlig, maxLeveringstidDager } = summerArbeidForKapasitet(formData.arbeid);
            const forslag = computeFerdigDatoForskslag({
                cap,
                verkstedSlug,
                overridesAll,
                perDagMin: brukt?.perDagMin || {},
                dropoffDato: today,
                totalMin,
                maxLeveringstidDager,
                samlesUkentlig,
            });

            if (forslag?.dato) {
                setFormData((prevData) => ({
                    ...prevData,
                    ferdigDato: isoDate(forslag.dato),
                }));
                setFerdigdatoMelding(
                    samlesUkentlig
                        ? 'Foreslått ferdigdato basert på slip-planen for butikken.'
                        : 'Foreslått ferdigdato basert på leveringstid og nåværende kø.'
                );
            } else {
                setFerdigdatoMelding('Fant ingen ledig ferdigdato de nærmeste ukene.');
            }
        } catch (error) {
            console.error('Feil ved beregning av ferdigdato:', error);
            setFerdigdatoMelding('Noe gikk galt under beregningen.');
        } finally {
            setBeregnerFerdigdato(false);
        }
    };

    const handleCopyWork = (indexToCopy) => {
        // Log hele arbeid-arrayen fra serviceDetails for å inspisere hva som er lagret
        console.log('Arbeid array fra serviceDetails:', serviceDetails?.arbeid);

        // Velg det arbeidet som er på den angitte indeksen
        const selectedWork = serviceDetails?.arbeid[indexToCopy]; // Bruk serviceDetails i stedet for formData

        // Log det valgte arbeidet for å inspisere om det har en beskrivelse
        console.log('Valgt arbeid for kopiering:', selectedWork);

        if (selectedWork && selectedWork.description) {
            // Log hva som blir kopiert hvis det er en beskrivelse
            console.log('Kopierer beskrivelse fra backend:', selectedWork.description);

            // Oppdater utført arbeid med beskrivelse fra det valgte arbeidet
            const newUtførtArbeid =
                `${selectedWork.description}\n\n${formData.utførtArbeid}`.trim();

            setFormData((prevData) => ({
                ...prevData,
                utførtArbeid: newUtførtArbeid,
            }));

            // Log den oppdaterte verdien for utført arbeid
            console.log('Oppdatert utført arbeid med kopi:', newUtførtArbeid);
        } else {
            // Hvis beskrivelsen mangler, log en feilmelding
            console.error('Ingen beskrivelse tilgjengelig for det valgte arbeidet.');
        }
    };

    // Hent servicedetaljer
    useEffect(() => {
        const fetchService = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/services/${id}`);
                if (response.ok) {
                    const service = await response.json();
                    console.log('Hentet service fra backend:', service);

                    // Split ferdigDato til dato og klokkeslett
                    const isoDate = service.ferdigDato;
                    const datePart = isoDate ? isoDate.split('T')[0] : '';
                    const timePart = isoDate ? isoDate.split('T')[1].slice(0, 5) : '';

                    console.log('Splittet ferdigDato:');
                    console.log('Dato:', datePart);
                    console.log('Klokkeslett:', timePart);

                    setServiceDetails(service);

                    setFormData({
                        beskrivelse: service.Beskrivelse || '',
                        arbeid: service.arbeid || [],
                        deler: service.deler || [], // Endret her for å hente delene fra service
                        utførtArbeid: service.utførtArbeid || '', // Hent utførtArbeid
                        status: service.status || 'Aktiv',
                        ansatt: service.ansatt || '',
                        Varemerke: service.Varemerke || '',
                        Produkt: service.Produkt || '',
                        Størrelse: service.Størrelse || '',
                        Farge: service.Farge || '',
                        ferdigDato: datePart,
                        ferdigTid: timePart,
                        servicetype: service.servicetype || 'Skiservice',
                    });

                    // Sett isDescriptionEmpty basert på den hentede beskrivelsen
                    if (!service.Beskrivelse || !service.Beskrivelse.trim()) {
                        setIsDescriptionEmpty(true);
                    } else {
                        setIsDescriptionEmpty(false);
                    }

                    if (service.kundeid) {
                        fetchCustomer(service.kundeid);
                    }
                } else {
                    console.error('Tjeneste ble ikke funnet');
                }
            } catch (error) {
                console.error('Feil ved henting av tjenesten:', error);
            }
        };

        const fetchCustomer = async (kundeid) => {
            try {
                const response = await fetch(`${API_BASE_URL}/customers?customerNumber=${encodeURIComponent(kundeid)}`);
                if (response.ok) {
                    const customerData = await response.json();
                    const customer = customerData.find((c) => c.customerNumber === kundeid);
                    if (customer) {
                        setCustomer(customer);
                    }
                }
            } catch (error) {
                console.error('Feil ved henting av kunden:', error);
            }
        };

        fetchService();
    }, [id]);

    // Hent ansatte
    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/employees`);
                if (response.ok) {
                    const employeesData = await response.json();
                    setEmployees(employeesData);
                }
            } catch (error) {
                console.error('Feil ved henting av ansatte:', error);
            }
        };
        fetchEmployees();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSaveFields = async () => {
        const updatedFields = {
            Varemerke: formData.Varemerke,
            Produkt: formData.Produkt,
            Størrelse: formData.Størrelse,
            Farge: formData.Farge,
        };

        console.log('Lagrer følgende felt i databasen:', updatedFields);

        try {
            const response = await fetch(`${API_BASE_URL}/services/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedFields),
            });

            if (response.ok) {
                console.log('Oppdatering vellykket');
                setUpdateMessage('Produktinfo oppdatert');
            } else {
                console.error('Feil ved oppdatering av produktinfo:', response.statusText);
                console.log('Response status:', response.status);
            }
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Sjekk om beskrivelse er tom
        if (!formData.beskrivelse.trim()) {
            setIsDescriptionEmpty(true);
            return;
        }

        setIsDescriptionEmpty(false);

        // Konverter datoer til ISO-format hvis de finnes
        const ferdigDatoISO = formData.ferdigDato
            ? `${formData.ferdigDato}T${formData.ferdigTid || '00:00'}:00.000Z`
            : null;

        const registrertDatoISO = formData.registrertDato
            ? new Date(formData.registrertDato).toISOString()
            : null;

        const endretdatoISO = new Date().toISOString();

        console.log(
            formData.ferdigDato
                ? `ferdigDato er satt til: ${ferdigDatoISO}`
                : 'ferdigDato er tomt og vil bli satt til null.'
        );

        // Opprett objekt for oppdatering
        const updatedService = {
            ...formData,
            Beskrivelse: formData.beskrivelse,
            ferdigDato: ferdigDatoISO, // Bruk ISO-format eller null
            registrertDato: registrertDatoISO, // Konverter til ISO-format
            endretdato: endretdatoISO, // Send alltid som ISO-format
            servicetype: formData.servicetype,
        };

        console.log('Objekt sendt til server (handleSubmit):', updatedService);

        try {
            const response = await fetch(`${API_BASE_URL}/services/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedService),
            });

            console.log('HTTP status:', response.status);
            if (response.ok) {
                const updatedResponse = await response.json();
                console.log('Respons fra server etter oppdatering:', updatedResponse);
                setUpdateMessage('Tjenesten er oppdatert');
            } else {
                const errorResponse = await response.text();
                console.error('Feil ved oppdatering:', response.statusText, errorResponse);
            }
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
        }
    };

    const handleSubmitAndClose = async (e) => {
        e.preventDefault();

        // Sjekk om beskrivelse er tom
        if (!formData.beskrivelse.trim()) {
            setIsDescriptionEmpty(true);
            return;
        }

        setIsDescriptionEmpty(false);

        // Konverter datoer til ISO-format hvis de finnes
        const ferdigDatoISO = formData.ferdigDato
            ? `${formData.ferdigDato}T${formData.ferdigTid || '00:00'}:00.000Z`
            : null;

        const registrertDatoISO = formData.registrertDato
            ? new Date(formData.registrertDato).toISOString()
            : null;

        const endretdatoISO = new Date().toISOString();

        console.log(
            formData.ferdigDato
                ? `ferdigDato er satt til: ${ferdigDatoISO}`
                : 'ferdigDato er tomt og vil bli satt til null.'
        );

        // Opprett objekt for oppdatering
        const updatedService = {
            ...formData,
            ferdigDato: ferdigDatoISO, // Bruk ISO-format eller null
            registrertDato: registrertDatoISO, // Konverter til ISO-format
            endretdato: endretdatoISO, // Send alltid som ISO-format
        };

        console.log('Objekt sendt til server (handleSubmitAndClose):', updatedService);

        try {
            const response = await fetch(`${API_BASE_URL}/services/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedService),
            });

            console.log('HTTP status:', response.status);
            if (response.ok) {
                const updatedResponse = await response.json();
                console.log('Respons fra server etter oppdatering:', updatedResponse);
                navigate('/service'); // Naviger til ServiceList etter oppdatering
            } else {
                const errorResponse = await response.text();
                console.error('Feil ved oppdatering:', response.statusText, errorResponse);
            }
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
        }
    };

    // Legg til egendefinert arbeid
    const handleAddCustomWork = async () => {
        if (customWork.title && customWork.price) {
            const updatedWork = [
                ...formData.arbeid,
                { title: customWork.title, price: parseFloat(customWork.price) },
            ];

            // Oppdater front-end
            setFormData((prevData) => ({ ...prevData, arbeid: updatedWork }));

            // Tøm feltene etter at arbeidet er lagt til
            setCustomWork({ title: '', price: '' });

            // Send oppdatering til backend
            try {
                const updatedService = {
                    arbeid: updatedWork.map((work) => ({
                        title: work.title,
                        price: work.price,
                    })),
                };

                const response = await fetch(`${API_BASE_URL}/services/${id}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updatedService),
                });

                if (!response.ok) {
                    console.error(
                        'Feil ved oppdatering av arbeid på serveren:',
                        response.statusText
                    );
                } else {
                    console.log('Egendefinert arbeid lagret');
                }
            } catch (error) {
                console.error('Feil ved kommunikasjon med serveren:', error);
            }
        }
    };

    // Funksjon for å legge til egendefinert del
    const handleAddCustomPart = async () => {
        if (
            customPart.ean &&
            customPart.brand &&
            customPart.product &&
            customPart.price &&
            customPart.numberof
        ) {
            const updatedParts = [
                ...formData.deler,
                {
                    ean: customPart.ean,
                    brand: customPart.brand,
                    product: customPart.product,
                    price: parseFloat(customPart.price),
                    discount: parseFloat(customPart.discount || 0),
                    numberof: parseInt(customPart.numberof || 1),
                },
            ];

            // Oppdater front-end
            setFormData((prevData) => ({
                ...prevData,
                deler: updatedParts,
            }));

            // Tøm feltene etter at delen er lagt til
            setCustomPart({ ean: '', brand: '', product: '', price: '', discount: '' });

            // Send oppdatering til backend
            try {
                const updatedService = {
                    deler: updatedParts.map((part) => ({
                        ean: part.ean,
                        brand: part.brand,
                        product: part.product,
                        price: part.price,
                        discount: part.discount,
                    })),
                };

                const response = await fetch(`${API_BASE_URL}/services/${id}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updatedService),
                });

                if (!response.ok) {
                    console.error(
                        'Feil ved oppdatering av deler på serveren:',
                        response.statusText
                    );
                } else {
                    console.log('Egendefinert del lagret');
                }
            } catch (error) {
                console.error('Feil ved kommunikasjon med serveren:', error);
            }
        }
    };

    const saveServiceDetails = async () => {
        const { arbeid, ...updatedFields } = formData;

        const updatedService = {
            ...updatedFields,
            servicetype: formData.servicetype,
            Beskrivelse: formData.beskrivelse,
            utførtArbeid: formData.utførtArbeid,
            Varemerke: formData.Varemerke,
            Produkt: formData.Produkt,
            Størrelse: formData.Størrelse,
            Farge: formData.Farge,
            status: formData.status,
            ansatt: formData.ansatt,
            endretdato: new Date().toLocaleString('no-NO', { timeZone: 'Europe/Oslo' }),
        };

        try {
            const response = await fetch(`${API_BASE_URL}/services/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedService),
            });

            if (response.ok) {
                setUpdateMessage('Tjenesten er oppdatert');
                console.log('Oppdatert tjeneste:', updatedService);
                return true; // Return true hvis lagringen var vellykket
            } else {
                console.error('Feil ved oppdatering av tjeneste:', response.statusText);
                return false;
            }
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
            return false;
        }
    };

    const handlePrintLabel = async () => {
        const savedSuccessfully = await saveServiceDetails();

        if (savedSuccessfully && customer && serviceDetails) {
            const printWindow = window.open('', '', 'width=500,height=300');

            const today = new Date();
            const formattedDate = `${today.getDate().toString().padStart(2, '0')}.${(
                today.getMonth() + 1
            )
                .toString()
                .padStart(2, '0')}.${today.getFullYear()}`;

            printWindow.document.write(`
            <html>
                <head>
                    <style>
                        @page {
                            size: 90mm 29mm;
                            margin: 0;
                        }
                        @media print {
                            body {
                                width: 90mm;
                                height: 29mm;
                                margin: 0;
                                padding: 0;
                                overflow: hidden;
                                display: flex;
                                flex-direction: column;
                                justify-content: center;
                                align-items: center;
                                font-family: Arial, sans-serif;
                            }
                        }
                        .customer-name {
                            font-size: 10px;
                            text-align: center;
                        }
                        .order-id {
                            font-size: 20px;
                            text-align: center;
                            font-weight: bold;
                        }
                    </style>
                </head>
                <body>
                    <div class="customer-name">${escapeHtml(customer.firstName)} ${escapeHtml(customer.lastName)} (${formattedDate})</div>
                    <div class="order-id">Servicenummer: ${serviceDetails.serviceid}</div>
                </body>
            </html>
        `);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }
    };

    const openPrintWindow = (serviceDetails, customer, formData, calculateTotalPrice) => {
        const newWindow = window.open('', '_blank');

        // Sjekk at .print-invoice finnes i DOM-en før du fortsetter
        const printInvoiceElement = document.getElementsByClassName('print-invoice')[0];
        if (!printInvoiceElement) {
            console.error('Kan ikke finne print-invoice elementet');
            return;
        }

        // Skriv HTML til det nye vinduet, inkludert Tailwind CSS fra et CDN eller din egen bygde versjon
        newWindow.document.write(`
    <html>
      <head>
        <title>Service Faktura</title>
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
        <style>
          /* Inline CSS for additional styling, if necessary */
          body {
            width: 210mm; /* A4 bredde */
            height: 297mm; /* A4 høyde */
            padding: 20mm;
            font-family: Arial, sans-serif;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid black;
            padding: 5px;
          }
          img {
            width: 100px;
          }
        </style>
      </head>
      <body>
        ${printInvoiceElement.outerHTML}
      </body>
    </html>
  `);

        // Vent til vinduet er lastet, deretter fokuser og skriv ut
        newWindow.onload = () => {
            newWindow.focus();
            newWindow.print();
        };

        // Lukk dokumentet når innholdet er skrevet
        newWindow.document.close();
    };

    // Funksjon for å håndtere utskrift
    const handlePrint = () => {
        openPrintWindow(serviceDetails, customer, formData, calculateTotalPrice);
    };

    // Funksjoner for å beregne totalpris
    const calculateTotalWorkPrice = () => {
        return formData.arbeid.reduce((total, work) => total + work.price, 0);
    };

    const totalPartsPrice = calculateTotalPartsPrice(formData.deler);

    const calculateTotalPrice = () => {
        return calculateTotalWorkPrice() + totalPartsPrice;
    };

    const handleSendSMS = () => {
        navigate('/sendsms', { state: { serviceDetails, customer } });
    };

    // Hvis siden er i ferd med å laste, vis et lasteskjermbilde
    if (isLoading) {
        return <div className='text-center py-10'>Laster inn data...</div>;
    }

    return (
        <div className='max-w-5xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6 mb-4'>
            {/* Dynamisk tittel med servicetype */}
            <h2 className='text-3xl font-bold mb-6 text-center text-gray-800'>
                {serviceDetails?.servicetype ? `${serviceDetails.servicetype}` : 'Tjenestedetaljer'}
            </h2>

            {/* Samlet ramme for registrert dato, endret dato og kundedetaljer */}
            {serviceDetails && customer && (
                <ServiceCustomerInfo serviceDetails={serviceDetails} customer={customer} />
            )}

            <div className='flex justify-between mb-6 hidden md:flex'>
                <button
                    onClick={handlePrintLabel}
                    className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600'
                >
                    Skriv ut label (Servicenr: {serviceDetails?.serviceid})
                </button>

                <button
                    onClick={handleSendSMS}
                    className='bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600'
                >
                    Send SMS
                </button>
            </div>

            <form onSubmit={handleSubmit} className='space-y-4'>
                <ServiceProductInfo
                    formData={formData}
                    handleChange={handleChange}
                    isEditable={isEditable}
                    setIsEditable={setIsEditable}
                    handleSaveFields={handleSaveFields}
                />

                {/* Kundebeskrivelse */}
                <CustomerServiceDescription
                    formData={formData}
                    setFormData={setFormData}
                    isDescriptionEmpty={isDescriptionEmpty}
                />

                <div className='p-4 border border-gray-300 rounded-lg'>
                    <label className='block text-sm font-medium text-gray-700'>
                        Velg arbeid fra listen:
                    </label>
                    <div className='flex items-center space-x-4 mt-2'>
                        <select
                            onChange={handleSelectWork}
                            className='block w-full p-2 border border-gray-300 rounded-md'
                        >
                            <option value=''>Velg arbeid</option>
                            {fixedPrices
                                .filter((price) => price.serviceType === 'Skiservice')
                                .sort((a, b) => a.priority - b.priority)
                                .map((price) => (
                                    <option key={price._id} value={price._id}>
                                        {price.title} - {price.price} kr
                                    </option>
                                ))}
                        </select>
                        <button
                            type='button'
                            onClick={handleAddSelectedWork}
                            disabled={!selectedWork}
                            className={`px-4 py-2 rounded whitespace-nowrap ${selectedWork
                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            Legg til arbeid
                        </button>
                    </div>
                    {isWorkEmpty && <p className='text-red-500 text-sm mt-2'>Legg til arbeid.</p>}
                </div>

                {/* Valgt arbeid, deler, priser og knapp */}
                {!isWorkEmpty && (
                    <div className='p-4 border border-gray-300 rounded-lg'>
                        {/* Arbeid */}
                        <h3 className='text-lg font-semibold mb-4'>Arbeid:</h3>
                        <ul>
                            {formData.arbeid.map((item, index) => (
                                <li key={index} className='flex justify-between items-center'>
                                    <span>
                                        {item.title} - {item.price} kr
                                    </span>
                                    <div className='flex space-x-2'>
                                        <button
                                            type='button'
                                            onClick={() => handleCopyWork(index)}
                                            className='text-blue-500 hover:text-blue-700'
                                        >
                                            Kopi
                                        </button>
                                        <button
                                            type='button'
                                            onClick={async () => {
                                                // Oppdaterer kun state
                                                const updatedWork = formData.arbeid.filter(
                                                    (_, i) => i !== index
                                                );
                                                setFormData((prevData) => ({
                                                    ...prevData,
                                                    arbeid: updatedWork,
                                                }));

                                                // Oppdater backend
                                                try {
                                                    const updatedService = { arbeid: updatedWork };
                                                    await fetch(`${API_BASE_URL}/services/${id}`, {
                                                        method: 'PATCH',
                                                        headers: {
                                                            'Content-Type': 'application/json',
                                                        },
                                                        body: JSON.stringify(updatedService),
                                                    });
                                                } catch (error) {
                                                    console.error(
                                                        'Feil ved fjerning av arbeid:',
                                                        error
                                                    );
                                                }
                                            }}
                                            className='text-red-500 hover:text-red-700'
                                        >
                                            Fjern
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <p className='text-lg font-bold mt-4 mb-4 text-left'>
                            Totalt arbeid: {calculateTotalWorkPrice()} kr
                        </p>

                        {/* Deler */}
                        <h3 className='text-lg font-semibold mb-4'>Deler:</h3>
                        <ul>
                            {formData.deler.map((part, index) => (
                                <li key={index} className='flex justify-between items-center'>
                                    <span>
                                        {part.ean} - {part.product} - {part.price} kr
                                    </span>
                                    <button
                                        type='button'
                                        onClick={async () => {
                                            // Oppdaterer kun state
                                            const updatedParts = formData.deler.filter(
                                                (_, i) => i !== index
                                            );
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                deler: updatedParts,
                                            }));

                                            // Oppdater backend
                                            try {
                                                const updatedService = { deler: updatedParts };
                                                await fetch(`${API_BASE_URL}/services/${id}`, {
                                                    method: 'PATCH',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify(updatedService),
                                                });
                                            } catch (error) {
                                                console.error('Feil ved fjerning av del:', error);
                                            }
                                        }}
                                        className='text-red-500 hover:text-red-700'
                                    >
                                        Fjern
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <p className='text-lg font-bold mt-4 text-left'>
                            Totalt deler: {totalPartsPrice} kr
                        </p>

                        {/* Totalpris */}
                        <p className='text-lg font-bold mt-4 text-right'>
                            Totalpris: {calculateTotalPrice()} kr
                        </p>

                        {/* Knapp for å generere PDF */}
                        <button
                            onClick={handlePrint}
                            className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mt-4'
                        >
                            Skriv ut fakturaside
                        </button>

                        {/* Render den utskriftsvennlige komponenten i bakgrunnen (ikke synlig) */}
                        <div style={{ display: 'none' }}>
                            <PrintInvoice
                                serviceDetails={serviceDetails}
                                customer={customer}
                                formData={formData}
                                calculateTotalPrice={calculateTotalPrice}
                            />
                        </div>
                    </div>
                )}

                {/* Søkefelt for deler */}
                <div className='p-4 border border-gray-300 rounded-lg'>
                    <label className='block text-sm font-medium text-gray-700'>
                        Søk etter deler (EAN, Merke, Produkt):
                    </label>
                    <input
                        type='text'
                        value={searchTerm}
                        onChange={handleSearchParts}
                        placeholder='Skriv inn minst 2 bokstaver for å søke etter deler'
                        className='mt-2 block w-full p-2 border border-gray-300 rounded-md'
                    />

                    {/* Vise filtrerte deler som nedtrekksliste */}
                    {filteredParts.length > 0 && (
                        <ul className='mt-2 border border-gray-300 rounded-md'>
                            {filteredParts.map((part, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleAddPartToWork(part)}
                                    className='cursor-pointer p-2 hover:bg-gray-100'
                                >
                                    {part.ean} - {part.product} - {part.brand} - {part.price} kr
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Egendefinert arbeid */}
                    <label className='block text-sm font-medium text-gray-700'>
                        Legg til egendefinert arbeid:
                    </label>
                    <div className='mt-2 flex space-x-4'>
                        <input
                            type='text'
                            placeholder='Arbeid'
                            value={customWork.title}
                            onChange={(e) =>
                                setCustomWork({ ...customWork, title: e.target.value })
                            }
                            className='block w-full p-2 border border-gray-300 rounded-md'
                        />
                        <input
                            type='number'
                            placeholder='Pris'
                            value={customWork.price}
                            onChange={(e) =>
                                setCustomWork({ ...customWork, price: e.target.value })
                            }
                            className='block w-1/2 p-2 border border-gray-300 rounded-md'
                        />
                        <button
                            type='button'
                            onClick={handleAddCustomWork}
                            className='bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600'
                        >
                            +
                        </button>
                    </div>

                    <label className='block text-sm font-medium text-gray-700'>
                        Legg til egendefinert del:
                    </label>

                    {/* Alle feltene på én linje */}
                    <div className='mt-2 flex space-x-4 flex-wrap md:flex-nowrap'>
                        <label className='grid gap-1 items-center'>
                            <span className='text-xs'>EAN:</span>
                            <input
                                type='text'
                                value={customPart.ean}
                                onChange={(e) =>
                                    setCustomPart({ ...customPart, ean: e.target.value })
                                }
                                placeholder='EAN/PLU'
                                className='block w-full p-2 border border-gray-300 rounded-md'
                            />
                        </label>
                        <label className='grid gap-1 items-center'>
                            <span className='text-xs'>Varemerke:</span>
                            <input
                                type='text'
                                placeholder='Varemerke'
                                value={customPart.brand}
                                onChange={(e) =>
                                    setCustomPart({ ...customPart, brand: e.target.value })
                                }
                                className='block w-full p-2 border border-gray-300 rounded-md'
                            />
                        </label>
                        <label className='grid gap-1 items-center'>
                            <span className='text-xs'>Produkt:</span>
                            <input
                                type='text'
                                placeholder='Produkt'
                                value={customPart.product}
                                onChange={(e) =>
                                    setCustomPart({
                                        ...customPart,
                                        product: e.target.value,
                                    })
                                }
                                className='block w-full p-2 border border-gray-300 rounded-md'
                            />
                        </label>
                        <div className='md:w-2/5 flex gap-2'>
                            <label className='w-3/5  grid gap-1 items-center'>
                                <span className='text-xs'>Pris:</span>
                                <input
                                    type='number'
                                    placeholder='Pris'
                                    value={customPart.price}
                                    onChange={(e) =>
                                        setCustomPart({
                                            ...customPart,
                                            price: e.target.value,
                                        })
                                    }
                                    className='block w-full p-2 border border-gray-300 rounded-md text-end'
                                />
                            </label>
                            <label className='w-2/5 grid gap-1 items-center'>
                                <span className='text-xs'>%</span>
                                <input
                                    type='number'
                                    placeholder='Rabatt (%)'
                                    value={customPart.discount}
                                    onChange={(e) =>
                                        setCustomPart({
                                            ...customPart,
                                            discount: e.target.value,
                                        })
                                    }
                                    className='block w-full p-2 border border-gray-300 rounded-md text-end'
                                />
                            </label>
                            <label className='w-2/5 grid gap-1 items-center'>
                                <span className='text-xs'>Antall:</span>
                                <input
                                    type='number'
                                    placeholder='Antall'
                                    value={customPart.numberof}
                                    onChange={(e) =>
                                        setCustomPart({
                                            ...customPart,
                                            numberof: e.target.value,
                                        })
                                    }
                                    className='block w-full p-2 border border-gray-300 rounded-md text-end'
                                />
                            </label>
                            <button
                                type='button'
                                onClick={handleAddCustomPart}
                                className='bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 h-11 w-11 flex items-center justify-center self-end'
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>

                <div className='p-4 border border-gray-300 rounded-lg'>
                    <div>
                        <label className='block text-sm font-medium text-gray-700'>
                            Utført arbeid:
                        </label>
                        <textarea
                            name='utførtArbeid'
                            value={formData.utførtArbeid}
                            onChange={handleChange}
                            rows='7'
                            className='mt-1 block w-full p-2 border border-gray-300 rounded-md resize-y'
                        />
                    </div>
                </div>
                <div className='p-4 border border-gray-300 rounded-lg'>
                    <div>
                        <label className='block text-sm font-medium text-gray-700'>Status:</label>
                        <select
                            name='status'
                            value={formData.status}
                            onChange={handleChange}
                            className='mt-1 block w-full p-2 border border-gray-300 rounded-md'
                        >
                            <option value='Aktiv'>Aktiv</option>
                            <option value='Avsluttet'>Avsluttet</option>
                        </select>
                    </div>
                    <div>
                        <label className='mt-4 block text-sm font-medium text-gray-700'>
                            Ansatt:
                        </label>
                        <select
                            name='ansatt'
                            value={formData.ansatt}
                            onChange={handleChange}
                            className='mt-1 block w-full p-2 border border-gray-300 rounded-md'
                        >
                            <option value=''>Velg ansatt</option>
                            {employees.map((employee) => (
                                <option key={employee._id} value={employee.navn}>
                                    {employee.navn}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className='p-4 border border-gray-300 rounded-lg'>
                    <label className='block text-sm font-medium text-gray-700'>Ferdig dato</label>
                    <div className='flex items-center space-x-4'>
                        <input
                            type='date'
                            name='ferdigDato'
                            value={formData.ferdigDato}
                            onChange={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    ferdigDato: e.target.value,
                                }))
                            }
                            className='mt-1 block w-full p-2 border border-gray-300 rounded-md'
                        />
                        <button
                            type='button'
                            onClick={handleForeslaFerdigdato}
                            disabled={beregnerFerdigdato}
                            className='mt-1 px-4 py-2 rounded whitespace-nowrap bg-indigo-500 text-white hover:bg-indigo-600 disabled:bg-gray-300 disabled:text-gray-500'
                        >
                            {beregnerFerdigdato ? 'Beregner…' : 'Foreslå ferdigdato'}
                        </button>
                    </div>
                    {ferdigdatoMelding && <p className='mt-1 text-xs text-gray-500'>{ferdigdatoMelding}</p>}

                    <label className='mt-4 block text-sm font-medium text-gray-700'>
                        Klokkeslett (valgfritt)
                    </label>

                    <input
                        type='time'
                        name='ferdigTid'
                        value={formData.ferdigTid}
                        onChange={(e) =>
                            setFormData((prevData) => ({
                                ...prevData,
                                ferdigTid: e.target.value,
                            }))
                        }
                        className='mt-1 block w-full p-2 border border-gray-300 rounded-md'
                    />
                </div>
                <ServiceTypeSelect
                    formData={formData}
                    handleChange={handleChange}
                    saveServiceDetails={saveServiceDetails}
                />
                <div className='text-center mt-6'>
                    <button
                        type='submit'
                        className='bg-green-500 text-white px-6 py-3 rounded hover:bg-green-600'
                    >
                        Oppdater Tjeneste
                    </button>
                    <button
                        onClick={handleSubmitAndClose}
                        className='bg-blue-500 text-white px-6 py-3 rounded hover:bg-blue-600 ml-4'
                    >
                        Lukk
                    </button>
                </div>
                {updateMessage && (
                    <p className='text-green-500 text-center mt-4'>{updateMessage}</p>
                )}
            </form>
        </div>
    );
}

export default ServiceDetails;
